import { ProConfigProvider } from '@ant-design/pro-components';
import { useTheme } from '@zhiyue/core';
import { App, ConfigProvider, type ThemeConfig, theme } from 'antd';
import React from 'react';
import { AppMessageBridge } from '@/utils/appMessage';

export type NavTheme = 'light' | 'realDark';

export const applyNavTheme = (navTheme?: string) => {
  if (typeof document === 'undefined') return;
  const dark = navTheme === 'realDark';
  document.documentElement.dataset.theme = dark ? 'dark' : 'light';
  document.documentElement.style.colorScheme = dark ? 'dark' : 'light';
};

export const getThemeAlgorithm: (dark: boolean) => ThemeConfig['algorithm'] = (
  dark: boolean,
) => (dark ? theme.darkAlgorithm : theme.defaultAlgorithm);

export const getNavigationToken = (dark: boolean) => {
  const background = dark ? '#1E1E1E' : '#F0F0F3';
  const text = dark ? 'rgba(255,255,255,0.88)' : '#1F2633';
  const muted = dark ? 'rgba(255,255,255,0.65)' : '#667085';
  const hover = dark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)';
  const selected = dark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.06)';
  return {
    header: {
      heightLayoutHeader: 44,
      colorBgHeader: background,
      colorBgScrollHeader: background,
      colorHeaderTitle: text,
      colorTextMenu: muted,
      colorTextMenuSelected: text,
      colorTextMenuActive: text,
      colorTextRightActionsItem: text,
      colorBgMenuItemSelected: 'transparent',
      colorBgMenuItemHover: 'transparent',
    },
    sider: {
      colorMenuBackground: background,
      colorTextMenu: muted,
      colorTextMenuSelected: text,
      colorTextMenuActive: text,
      colorTextSubMenuSelected: text,
      colorTextMenuItemHover: text,
      colorBgMenuItemHover: hover,
      colorBgMenuItemSelected: selected,
      colorTextCollapsedButton: muted,
      colorTextCollapsedButtonHover: text,
    },
    pageContainer: {
      colorBgPageContainer: dark ? '#121212' : '#ffffff',
    },
  };
};

export function getApplicationTheme(dark: boolean): ThemeConfig {
  return {
    inherit: false,
    algorithm: getThemeAlgorithm(dark),
    cssVar: { key: `crm-${dark ? 'dark' : 'light'}` },
    components: {
      Button: {
        defaultBg: dark ? '#2b2b2b' : '#eeeeee',
        defaultHoverBg: dark ? '#363636' : '#e2e2e2',
        defaultActiveBg: dark ? '#414141' : '#d6d6d6',
        defaultHoverColor: dark ? 'rgba(255,255,255,.85)' : '#1f1f1f',
        defaultActiveColor: dark ? 'rgba(255,255,255,.85)' : '#1f1f1f',
        defaultGhostColor: dark ? 'rgba(255,255,255,.85)' : '#1f1f1f',
        defaultGhostBorderColor: 'transparent',
        defaultBorderColor: 'transparent',
        defaultHoverBorderColor: 'transparent',
        defaultActiveBorderColor: 'transparent',
      },
      Menu: { colorPrimary: dark ? '#69b1ff' : '#1677ff' },
      Tabs: { colorPrimary: dark ? '#69b1ff' : '#1677ff' },
      Pagination: { colorPrimary: dark ? '#69b1ff' : '#1677ff' },
    },
    token: {
      colorPrimary: '#1677ff',
      colorPrimaryText: dark ? '#69b1ff' : '#1677ff',
      colorPrimaryTextHover: dark ? '#91caff' : '#0958d9',
      colorLink: dark ? '#69b1ff' : '#1677ff',
      colorLinkHover: dark ? '#91caff' : '#0958d9',
      colorLinkActive: dark ? '#91caff' : '#0958d9',
      colorBorder: dark ? '#3a3a3a' : '#d9d9d9',
      colorBorderSecondary: dark ? '#2a2a2a' : '#f0f0f0',
      colorFillAlter: dark ? '#1d1d1d' : '#fafafa',
      colorFillTertiary: dark ? '#242424' : '#f5f5f5',
      colorTextSecondary: dark ? 'rgba(255,255,255,.68)' : '#667085',
      colorTextTertiary: dark ? 'rgba(255,255,255,.65)' : '#8c8c8c',
      colorTextPlaceholder: dark ? '#707070' : '#8c8c8c',
      colorSuccessText: dark ? '#95de64' : '#389e0d',
      colorWarningText: dark ? '#ffd666' : '#d46b08',
      colorErrorText: dark ? '#ff7875' : '#cf1322',
      fontFamily: 'AlibabaSans, sans-serif',
      colorBgLayout: dark ? '#121212' : '#ffffff',
      colorBgContainer: dark ? '#141414' : '#ffffff',
      colorBgElevated: dark ? '#1f1f1f' : '#ffffff',
      borderRadius: 8,
      controlHeight: 32,
    },
  };
}

export default function PlatformThemeBoundary({
  children,
}: React.PropsWithChildren) {
  const { navTheme } = useTheme();
  const dark = navTheme === 'realDark';

  React.useEffect(() => {
    applyNavTheme(navTheme);
    ConfigProvider.config({
      holderRender: (node) => (
        <ConfigProvider variant="outlined" theme={getApplicationTheme(dark)}>
          {node}
        </ConfigProvider>
      ),
    });
  }, [dark, navTheme]);

  return (
    <ConfigProvider variant="outlined" theme={getApplicationTheme(dark)}>
      <ProConfigProvider dark={dark}>
        <App className="crm-content-theme">
          <AppMessageBridge />
          {children}
        </App>
      </ProConfigProvider>
    </ConfigProvider>
  );
}

export function useIsDarkMode() {
  const { navTheme } = useTheme();
  return navTheme === 'realDark';
}
